import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { KibCheckIcon } from '@chewy/kib-icons-react'
import { KibChewyLogo } from '@chewy/kib-logos-react'
import { Box, CircularProgress, Grid } from '@mui/material'
import classNames from 'classnames'
import * as R from 'ramda'
import { Defaults, Text, Utils } from '@pbt/pbt-ui-components'

import type { FilterSectionData } from '~/components/common/filters/MultiSectionFiltersFacetsWidget/FilterSections'
import FiltersAndFacetsWidget from '~/components/dashboard/soapV2/charges/tabs/search/FiltersAndFacetsWidget'
import {
  DEFAULT_CHARGE_SEARCH_FILTERS,
  PatientFilterType,
} from '~/constants/chargeSearchFilters'
import { OrderType } from '~/constants/SOAPStates'
import { updatePatientRestrictions } from '~/store/actions/orders'
import { getInventoryCategory } from '~/store/reducers/constants'
import {
  getOrdersIsReceiving,
  getOrdersList,
  getOrdersSearchResults,
  getOrdersSearchResultsTotalCount,
  getOrdersTotalCount,
  getPatientRestrictions,
  getSearchAggregations,
  type PatientRestrictionType,
} from '~/store/reducers/orders'
import { type Order, OrderListType } from '~/types'
import { transformAggregationsToFacets } from '~/utils/aggregationsUtils'

import { DEFAULT_INVENTORY_IDS } from '../hooks/useSwapProductSearch'

import style from './SwapRxSearchResults.module.scss'

const MIN_HEIGHT = 345

const DEFAULT_ORDER_LIST_TYPES = [OrderListType.SOLD_ON_CHEWY]
const FACET_TO_ORDER_TYPE_MAPPING = {
  Inventory: OrderType.INVENTORY,
} as const
const DEFAULT_PATIENT_RESTRICTIONS = {
  patient: [...DEFAULT_CHARGE_SEARCH_FILTERS.patient],
}
const getEnabledFilters = (filters: FilterSectionData) => {
  const enabledPatientFilters =
    filters.patient?.filter((filter) => filter.enabled) || []

  return enabledPatientFilters
    .map((filter) => filter.value as PatientRestrictionType)
    .filter((value): value is PatientRestrictionType =>
      Object.values(PatientFilterType).includes(value as PatientFilterType),
    )
}

const SwapRxSearchResults = ({
  searchTerm,
  onSearch,
  onSearchMore,
  selectedOrder,
  setSelectedOrder,
}: {
  onSearch: (value: string, restrictions?: PatientRestrictionType[]) => void
  onSearchMore: (
    value: string,
    from: number,
    to: number,
    restrictions: PatientRestrictionType[],
  ) => void
  searchTerm: string
  selectedOrder: Order | null
  setSelectedOrder: (selectedOrder: Order) => void
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Search')

  const isReceivingOrders = useSelector(getOrdersIsReceiving)
  const orders = useSelector(getOrdersList)
  const ordersTotalCount = useSelector(getOrdersTotalCount)
  const searchResults = useSelector(getOrdersSearchResults)
  const patientRestrictions = useSelector(getPatientRestrictions)
  const searchResultsTotalCount = useSelector(getOrdersSearchResultsTotalCount)
  const items = searchResults.map((result) => result.item)
  const searchAggregations = useSelector(getSearchAggregations)
  const InventoryCategory = useSelector(getInventoryCategory)
  const baseFacets = transformAggregationsToFacets(searchAggregations)
  const categoriesFacets =
    Object.keys(baseFacets).length > 0
      ? { Categories: baseFacets.Categories }
      : {}

  const infiniteScrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const filtersRef = useRef<HTMLDivElement>(null)

  const hasSearchedWithoutResults =
    Boolean(searchTerm) && items.length === 0 && !isReceivingOrders

  const hasMinSearchLength = searchTerm.length < Defaults.MIN_SEARCH_LENGTH

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [facetSelections, setFacetSelections] = useState<FilterSectionData>({})
  const [filterSelections, setFilterSelections] = useState<FilterSectionData>(
    DEFAULT_PATIENT_RESTRICTIONS,
  )
  const [selectedInventoryCategoryIds, setSelectedInventoryCategoryIds] =
    useState<string[]>(DEFAULT_INVENTORY_IDS)

  const facets = useMemo(() => {
    const mergedFacets: FilterSectionData = {}

    Object.keys(categoriesFacets).forEach((sectionKey) => {
      mergedFacets[sectionKey] = baseFacets[sectionKey].map((facet) => {
        const selectedFacet = facetSelections[sectionKey]?.find(
          (selected) => selected.id === facet.id,
        )

        return {
          ...facet,
          enabled: selectedFacet?.enabled || false,
        }
      })
    })

    return mergedFacets
  }, [baseFacets, facetSelections])

  const handleFiltersChange = (filters: FilterSectionData) => {
    const restrictions = getEnabledFilters(filters)
    setFilterSelections(filters)

    dispatch(updatePatientRestrictions(restrictions))
    onSearch(searchTerm, restrictions)
  }

  const prevSearchTermValue = useRef<string>('')

  useEffect(() => {
    const currentLength = searchTerm.length
    const prevLength = prevSearchTermValue.current.length
    const wasValidSearch = prevLength >= Defaults.MIN_SEARCH_LENGTH
    const isNowBelowMin = currentLength < Defaults.MIN_SEARCH_LENGTH
    const searchChanged = searchTerm !== prevSearchTermValue.current

    if (searchChanged && wasValidSearch && isNowBelowMin) {
      setFacetSelections({})
      setFilterSelections(DEFAULT_PATIENT_RESTRICTIONS)
      setSelectedCategories([])
      handleFiltersChange(DEFAULT_PATIENT_RESTRICTIONS)
      setSelectedInventoryCategoryIds(DEFAULT_INVENTORY_IDS)
    }

    prevSearchTermValue.current = searchTerm
  }, [searchTerm])

  const onFetchMore = useCallback(() => {
    onSearchMore(
      searchTerm,
      items.length,
      items.length + Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
      getEnabledFilters(filterSelections),
    )
  }, [
    items.length,
    patientRestrictions,
    searchTerm,
    DEFAULT_ORDER_LIST_TYPES,
    selectedInventoryCategoryIds,
    selectedCategories,
  ])

  const handleFacetsChange = (updatedFacets: FilterSectionData) => {
    const newCategories: string[] = []
    const newInventoryCategoryIds: string[] = []
    if (updatedFacets.Categories) {
      updatedFacets.Categories.filter((facet) => facet.enabled).forEach(
        (facet) => {
          const orderTypeValue =
            FACET_TO_ORDER_TYPE_MAPPING[
              facet.value as keyof typeof FACET_TO_ORDER_TYPE_MAPPING
            ]
          if (orderTypeValue) {
            newCategories.push(orderTypeValue)
          } else {
            const categoryId = Utils.findConstantIdByName(
              facet.value,
              InventoryCategory,
            )
            if (categoryId) {
              newInventoryCategoryIds.push(categoryId)
            }
          }
        },
      )
    }

    setSelectedInventoryCategoryIds(newInventoryCategoryIds)
    setSelectedCategories(newCategories)

    setFacetSelections(updatedFacets)

    onSearch(searchTerm, getEnabledFilters(filterSelections))
  }

  const hasMoreItems = R.isNil(searchResultsTotalCount)
    ? orders.length < ordersTotalCount
    : searchResults.length < searchResultsTotalCount

  return (
    <Grid
      container
      item
      className={style.mainWrapper}
      height={R.isEmpty(items) ? 'auto' : MIN_HEIGHT}
      position="absolute"
      ref={containerRef}
      width="100%"
      wrap="nowrap"
      zIndex={0}
    >
      <Box
        className={classNames(style.listContainer, {
          [style.hide]: hasMinSearchLength || selectedOrder !== null,
        })}
      >
        <Box display="flex" height="100%">
          <Box className={style.filterPanel} ref={filtersRef}>
            <FiltersAndFacetsWidget
              facets={facets}
              filters={filterSelections}
              resultsCount={
                searchResultsTotalCount ?? ordersTotalCount ?? items.length
              }
              onFacetsChange={handleFacetsChange}
              onFiltersChange={handleFiltersChange}
            />
          </Box>
          <Box className={style.contentPanel}>
            <Box
              id="scrollableDiv"
              ref={infiniteScrollRef}
              sx={{
                height: MIN_HEIGHT,
                overflowY: 'auto',
              }}
            >
              <InfiniteScroll
                className={classNames(style.infiniteScroll, {
                  [style.hide]: hasMinSearchLength,
                })}
                dataLength={items.length}
                hasMore={hasMoreItems}
                loader={
                  isReceivingOrders && (
                    <CircularProgress
                      color="secondary"
                      size={16}
                      sx={{
                        ml: 2,
                      }}
                    />
                  )
                }
                next={onFetchMore}
                scrollableTarget="scrollableDiv"
              >
                {isReceivingOrders && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <CircularProgress color="secondary" size={24} />
                  </Box>
                )}
                {hasSearchedWithoutResults && (
                  <Text px={2} variant="body">
                    {t('Search:NO_SEARCH_RESULTS')} &apos;
                    <Text strong component="span">
                      {searchTerm}
                    </Text>
                    &apos;
                  </Text>
                )}
                <div className={style.resultList}>
                  {!R.isEmpty(items) &&
                    items.map((order, index) => {
                      const splitIndex = order.name.indexOf('|')
                      const [firstSection, rest] =
                        splitIndex !== -1
                          ? [
                              order.name.slice(0, splitIndex),
                              order.name.slice(splitIndex),
                            ]
                          : [order.name, '']
                      return (
                        <div
                          className={style.resultItem}
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedOrder(order)}
                          onKeyUp={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setSelectedOrder(order)
                            }
                          }}
                        >
                          <strong>{firstSection}</strong>
                          {rest}
                          <br />
                          <span className={style.soldOnChewy}>
                            <KibCheckIcon className={style.check} /> Sold on
                            <KibChewyLogo
                              aria-label="Chewy"
                              className={style.chewyLogo}
                            />
                          </span>
                        </div>
                      )
                    })}
                </div>
              </InfiniteScroll>
            </Box>
          </Box>
        </Box>
      </Box>
    </Grid>
  )
}

export default SwapRxSearchResults
